/**
 * LayoutProvider — the board solver as a workspace-scoped service.
 *
 * `useBoardLayout` is the heaviest seam in the app: it turns the loaded model
 * into the renderable `root`, owns the global edge/port aggregation
 * (`globalCtx`), and drives all the drag / settle / animation machinery. App
 * used to call it inline; ArchitectureView called its OWN separate instance.
 * That meant the viewer chrome (the board, plus the details rail, which reads
 * `globalCtx`/`root`) and any board surface could solve the same workspace
 * twice.
 *
 * Promoting it to a provider gives a composition ONE layout instance: App reads
 * `root`/`globalCtx`/drag handlers from here, and the board (via BoardPane →
 * ArchitectureView's `layout` prop) reads the SAME controller. The inputs are
 * all self-sourced from the surrounding services — the shared compile
 * (WorkspaceModelProvider), the placements service (PlacementsProvider), and
 * the aspect web overlay (the store's labels.slice) — so mounting it is enough;
 * no props to thread.
 *
 * Mount it UNDER WorkspaceModelProvider + PlacementsProvider. Compositions with
 * no layout service (marketing demo, Studio preview) simply don't mount it —
 * `BoardPane` reads the optional accessor and ArchitectureView falls back to its
 * own local `useBoardLayout`.
 *
 * The solver itself is unchanged (this only lifts WHERE it's called), and its
 * byte-identical output is pinned by `board/__tests__/layout-snapshot-guard`.
 */
import { type ReactNode } from "react";
import { useBoardLayout } from "../hooks/useBoardLayout.js";
import { useDriftTopology } from "../board/driftTopologyStore.js";
import { useAspectOverlay } from "../hooks/useAspectOverlay.js";
import { useWorkspace } from "./WorkspaceContext.js";
import { useWorkspaceModel } from "./WorkspaceModelContext.js";
import { usePlacementsContext } from "./PlacementsContext.js";
import { LayoutContext } from "./LayoutContext.js";
import { useViewSurface } from "../views/useViewSurface.js";
import { ViewPlanStatusContext } from "../views/ViewPlanStatusContext.js";

export function LayoutProvider({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  const store = useWorkspace();
  const { model, edges, structural } = useWorkspaceModel();
  const { placements } = usePlacementsContext();
  // S15-live: the active view's projection plan (null = default identity board),
  // from the ONE shared surface computation (useViewSurface: tab-aware view
  // name, plane pick, live knobs, builder draft, the GAP-3 governance closure,
  // view-INSTANCE resolution, crash guard). The table/matrix containers consume
  // the SAME hook, so the board and the other representation surfaces can never
  // drift. `diagnostics` is the view's own health (surfaced by the HUD badge);
  // a projection crash degrades to the identity plan rather than unmounting.
  const { plan: viewPlan, enginePlan, diagnostics: viewPlanDiagnostics } = useViewSurface();
  // Aspect overlay folds synthetic nodes into the layout inputs. The active
  // view's `style @@key { render: replicated }` directive (enginePlan) selects
  // the projection mode per key, over the UI pick.
  const { aspectOverlay } = useAspectOverlay(store, model, enginePlan?.renderModeByAspect);
  // Live-conformance drift, published UP from the telemetry layer via the
  // module-level store (crosses the provider hierarchy). Empty when no telemetry
  // is wired → the board is byte-identical to the un-instrumented one.
  const undocumentedTopology = useDriftTopology();
  const layout = useBoardLayout({
    model,
    edges,
    aspectOverlay,
    placements,
    structural,
    viewPlan,
    undocumentedTopology,
  });
  return (
    <LayoutContext.Provider value={layout}>
      <ViewPlanStatusContext.Provider value={viewPlanDiagnostics}>
        {children}
      </ViewPlanStatusContext.Provider>
    </LayoutContext.Provider>
  );
}
